import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { API_BASE_URL } from '../config';
import Modal from './picture-ai/Modal';

const PERIODS = [7, 30, 90];
const SHAPE_LABELS = {
  standard: 'Standard filters only',
  ai_alone: 'AI filter alone',
  ai_with_period: 'AI filter with a period',
  ai_with_filters: 'AI filter with standard filters',
};

const cardClass = 'rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800';
const cellClass = 'px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300';
const headClass = 'px-3 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';

const millis = (value) => (value === null || value === undefined ? '—' : `${value} ms`);

const Panel = ({ title, hint, children }) => (
  <section className={cardClass}>
    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h2>
    {hint && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
    <div className="mt-3">{children}</div>
  </section>
);

const Unavailable = ({ children }) => (
  <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
    {children}
  </p>
);

const GESTURE_LABELS = {
  chip_removed: 'Chip removed',
  clarification_resolved: 'Clarification settled',
  clarification_dismissed: 'Clarification dismissed',
  suggestion_applied: 'Suggestion followed',
  results_failed: 'Gallery failed to load',
  search_reset: 'Search cleared',
};
const selectClass = 'rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200';
const pageButtonClass = 'rounded-md border border-gray-300 px-2.5 py-1 text-xs text-gray-700 disabled:opacity-40 dark:border-gray-600 dark:text-gray-300';

const resultsLabel = (search, failure, t) => {
  if (!search.applicable) return t('no filter');
  if (failure) return t(failure === 'query_timeout' ? 'timed out' : 'failed to load');
  return search.results_count ?? t('count unknown');
};

const describeGesture = (event, t) => {
  const { term, label, value, id, code } = event.detail || {};
  const subject = {
    chip_removed: label || value || event.field,
    clarification_resolved: `« ${term} » → ${label || value}`,
    clarification_dismissed: `« ${term} »`,
    suggestion_applied: [event.field, value || id].filter(Boolean).join(' · '),
    results_failed: code === 'query_timeout' ? t('timed out') : null,
  }[event.type];
  return [t(GESTURE_LABELS[event.type] || event.type), subject].filter(Boolean).join(' · ');
};

const SearchHistory =({ request, days, onOpen }) => {
  const { t } = useTranslation();
  const [client, setClient] = useState('');
  const filterKey = `${days}|${client}`;
  const [paging, setPaging] = useState({ key: filterKey, page: 1 });
  const page = paging.key === filterKey ? paging.page : 1;
  const [history, setHistory] = useState(null);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams({ days: `${days}`, page: `${page}` });
    if (client) params.set('client', client);
    setHistory(null);
    request(`/searches?${params}`)
      .then((payload) => {
        setHistory(payload);
        setClients(payload.clients || []);
      })
      .catch(() => setHistory(false));
  }, [request, days, client, page]);

  const everySearch = clients.reduce((sum, row) => sum + row.searches, 0);
  const clientChoices = clients.filter(row => row.client).map(row => [row.client, row.searches]);
  if (client && !clientChoices.some(([name]) => name === client)) clientChoices.push([client, 0]);
  const from = history?.total ? (history.page - 1) * history.pageSize + 1 : 0;
  const to = history ? Math.min(history.total, history.page * history.pageSize) : 0;

  return (
    <Panel
      title={t('All searches')}
      hint={t('Every search of the period, newest first, narrowed if need be to the users of one client.')}
    >
      <div className="mb-3 flex flex-wrap gap-2">
        <select value={client} onChange={event => setClient(event.target.value)} className={selectClass} aria-label={t('Client')}>
          <option value="">{t('All clients')} · {everySearch}</option>
          {clientChoices.map(([name, count]) => <option key={name} value={name}>{name} · {count}</option>)}
        </select>
      </div>

      {history === null && <p className="text-sm text-gray-500">{t('Loading…')}</p>}
      {history === false && <Unavailable>{t('Search history unavailable — run the migration.')}</Unavailable>}
      {history?.searches?.length === 0 && <p className="text-sm text-gray-500">{t('None over this period.')}</p>}
      {history?.searches?.length > 0 && (
        <>
          <table className="w-full">
            <thead>
              <tr>
                <th className={headClass}>{t('When')}</th>
                <th className={headClass}>{t('Question')}</th>
                <th className={headClass}>{t('Client')}</th>
                <th className={headClass}>{t('Results')}</th>
                <th className={headClass}>{t('Gestures')}</th>
                <th className={headClass}>{t('Duration')}</th>
              </tr>
            </thead>
            <tbody>
              {history.searches.map(row => (
                <tr
                  key={row.id}
                  onClick={() => onOpen(row.id)}
                  className="cursor-pointer border-t border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50"
                >
                  <td className={`${cellClass} whitespace-nowrap text-xs text-gray-500`}>{new Date(row.created_at).toLocaleString()}</td>
                  <td className={cellClass}>
                    {row.query}
                    <span className="ml-2 text-xs text-gray-400">{row.username}</span>
                  </td>
                  <td className={cellClass}>{row.client || '—'}</td>
                  <td className={cellClass}>{resultsLabel(row, row.results_failure, t)}</td>
                  <td className={cellClass}>{row.gestures || '—'}</td>
                  <td className={cellClass}>{millis(row.duration_ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
            <span>{t('{{from}}–{{to}} of {{total}}', { from, to, total: history.total })}</span>
            <div className="flex gap-1">
              <button type="button" className={pageButtonClass} disabled={page <= 1}
                onClick={() => setPaging({ key: filterKey, page: page - 1 })}>
                {t('Previous')}
              </button>
              <button type="button" className={pageButtonClass} disabled={to >= history.total}
                onClick={() => setPaging({ key: filterKey, page: page + 1 })}>
                {t('Next')}
              </button>
            </div>
          </div>
        </>
      )}
    </Panel>
  );
};

/**
 * What the two journals say about the search, read back rather than queried by hand.
 *
 * Both were written from the day they existed and neither was ever read: the analysis was SQL only.
 * The four panels answer the four questions the AI facets campaign puts — how long, what comes back
 * empty, what is actually used, and where the time goes inside one search.
 */
export default function SearchInsights({ fetchWithAuth }) {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const [source, setSource] = useState('search');
  const [latency, setLatency] = useState(null);
  const [shapes, setShapes] = useState(null);
  const [barren, setBarren] = useState(null);
  const [usage, setUsage] = useState(null);
  const [rated, setRated] = useState(null);
  const [timeouts, setTimeouts] = useState(null);
  const [detail, setDetail] = useState(null);

  const request = useCallback(async (path) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/search-insights${path}`);
    if (!response.ok) throw new Error(`${response.status}`);
    return response.json();
  }, [fetchWithAuth]);

  useEffect(() => {
    setLatency(null);
    request(`/latency?source=${source}&days=${days}`).then(setLatency).catch(() => setLatency([]));
  }, [request, source, days]);

  useEffect(() => {
    setShapes(null);
    setBarren(null);
    setUsage(null);
    setTimeouts(null);
    request(`/shapes?days=${days}`).then(setShapes).catch(() => setShapes([]));
    request(`/timeouts?days=${days}`).then(setTimeouts).catch(() => setTimeouts(false));
    request(`/barren?days=${days}`).then(setBarren).catch(() => setBarren([]));
    request(`/rated?days=${days}`).then(setRated).catch(() => setRated([]));
    request(`/usage?days=${days}`).then(setUsage).catch(() => setUsage({ dimensions: [], facets: [] }));
  }, [request, days]);

  const openDetail = async (id) => {
    setDetail({ loading: true });
    try {
      setDetail(await request(`/searches/${id}`));
    } catch {
      setDetail({ failed: true });
    }
  };

  return (
    <div className="max-w-6xl space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('Search performance')}</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('What the intelligent search and the AI filters actually cost, and what comes back empty.')}
          </p>
        </div>
        <div className="flex gap-1">
          {PERIODS.map(period => (
            <button
              key={period}
              type="button"
              onClick={() => setDays(period)}
              className={`rounded-md px-2.5 py-1 text-sm ${days === period
                ? 'bg-blue-600 text-white'
                : 'border border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300'}`}
            >
              {t('{{count}} days', { count: period })}
            </button>
          ))}
        </div>
      </header>

      <SearchHistory request={request} days={days} onOpen={openDetail} />

      <Panel
        title={t('Latency')}
        hint={t('Percentiles rather than an average: a page that is fine four times in five and unusable the fifth.')}
      >
        <div className="mb-2 flex gap-1">
          {['search', 'filters'].map(name => (
            <button
              key={name}
              type="button"
              onClick={() => setSource(name)}
              className={`rounded-md px-2.5 py-1 text-xs ${source === name
                ? 'bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900'
                : 'border border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-400'}`}
            >
              {name === 'search' ? t('Intelligent search') : t('AI filters')}
            </button>
          ))}
        </div>

        {latency === null && <p className="text-sm text-gray-500">{t('Loading…')}</p>}
        {latency?.length === 0 && (
          <Unavailable>{t('Nothing journalled yet for this period — or the migration has not been run.')}</Unavailable>
        )}
        {latency?.length > 0 && (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={latency.map(row => ({ ...row, day: `${row.day}`.slice(0, 10) }))}>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} unit=" ms" width={70} />
              <Tooltip />
              <Line type="monotone" dataKey="p50" stroke="#2563eb" dot={false} />
              <Line type="monotone" dataKey="p95" stroke="#f59e0b" dot={false} />
              <Line type="monotone" dataKey="p99" stroke="#dc2626" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Panel>

      <Panel
        title={t('By query shape')}
        hint={t('The comparison the campaign turns on: an AI predicate on its own, against the same one beside a standard filter.')}
      >
        {shapes === null && <p className="text-sm text-gray-500">{t('Loading…')}</p>}
        {shapes?.length === 0 && (
          <Unavailable>{t('No gallery query journalled yet — run the migration, then browse with a filter.')}</Unavailable>
        )}
        {shapes?.length > 0 && (
          <table className="w-full">
            <thead>
              <tr>
                <th className={headClass}>{t('Shape')}</th>
                <th className={headClass}>{t('Grain')}</th>
                <th className={headClass}>{t('Queries')}</th>
                <th className={headClass}>p50</th>
                <th className={headClass}>p95</th>
                <th className={headClass}>{t('of which counting')}</th>
              </tr>
            </thead>
            <tbody>
              {shapes.map((row, index) => (
                <tr key={index} className="border-t border-gray-100 dark:border-gray-700">
                  <td className={cellClass}>{t(SHAPE_LABELS[row.shape] || row.shape)}</td>
                  <td className={cellClass}><code className="text-xs text-gray-500">{(row.ai_scopes || []).join(', ') || '—'}</code></td>
                  <td className={cellClass}>{row.queries}</td>
                  <td className={cellClass}>{millis(row.p50)}</td>
                  <td className={cellClass}>{millis(row.p95)}</td>
                  <td className={cellClass}>{millis(row.count_p95)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel
        title={t('Timeouts')}
        hint={t('Queries cut by their time budget, per route. Filters are named, never their values: a route that keeps timing out is an index to add or a default to narrow.')}
      >
        {timeouts === null && <p className="text-sm text-gray-500">{t('Loading…')}</p>}
        {timeouts === false && <Unavailable>{t('Timeout journal unavailable — run the migration.')}</Unavailable>}
        {timeouts?.length === 0 && <p className="text-sm text-gray-500">{t('No timeout over this period.')}</p>}
        {timeouts?.length > 0 && (
          <table className="w-full">
            <thead>
              <tr>
                <th className={headClass}>{t('Route')}</th>
                <th className={headClass}>{t('Timeouts')}</th>
                <th className={headClass}>{t('With an AI filter')}</th>
                <th className={headClass}>{t('Users')}</th>
                <th className={headClass}>p50</th>
                <th className={headClass}>{t('Usual filters')}</th>
                <th className={headClass}>{t('Last seen')}</th>
              </tr>
            </thead>
            <tbody>
              {timeouts.map(row => (
                <tr key={`${row.method} ${row.route}`} className="border-t border-gray-100 dark:border-gray-700">
                  <td className={cellClass}><code className="text-xs">{row.method} {row.route}</code></td>
                  <td className={cellClass}>{row.timeouts}</td>
                  <td className={cellClass}>{row.with_ai}</td>
                  <td className={cellClass}>{row.users}</td>
                  <td className={cellClass}>{millis(row.p50)}</td>
                  <td className={cellClass}><code className="text-xs text-gray-500">{(row.usual_dimensions || []).join(', ') || '—'}</code></td>
                  <td className={cellClass}>{row.last_at ? new Date(row.last_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel
        title={t('Rated searches')}
        hint={t('What an administrator judged. A thumbs down carries the work to do; the comment says what was expected.')}
      >
        {rated === null && <p className="text-sm text-gray-500">{t('Loading…')}</p>}
        {rated?.length === 0 && <p className="text-sm text-gray-500">{t('None over this period.')}</p>}
        {rated?.length > 0 && (
          <table className="w-full">
            <thead>
              <tr>
                <th className={headClass}>{t('Question')}</th>
                <th className={headClass}>{t('Verdict')}</th>
                <th className={headClass}>{t('What was expected')}</th>
                <th className={headClass}>{t('Results')}</th>
              </tr>
            </thead>
            <tbody>
              {rated.map(row => (
                <tr
                  key={row.id}
                  onClick={() => openDetail(row.id)}
                  className="cursor-pointer border-t border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50"
                >
                  <td className={cellClass}>
                    {row.query}
                    <span className="ml-2 text-xs text-gray-400">{row.username}</span>
                  </td>
                  <td className={cellClass}>
                    <span className={row.rating === 1
                      ? 'rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200'
                      : 'rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/60 dark:text-amber-200'}>
                      {row.rating === 1 ? t('Right') : t('Wrong')}
                    </span>
                    <span className="ml-2 text-xs text-gray-400">{row.rated_by}</span>
                  </td>
                  <td className={`${cellClass} text-gray-600 dark:text-gray-300`}>{row.rating_comment || '—'}</td>
                  <td className={cellClass}>{row.applicable ? (row.results_count ?? '—') : t('no filter')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel
        title={t('Barren searches')}
        hint={t('A search that comes back empty says what the engine does not understand yet.')}
      >
        {barren === null && <p className="text-sm text-gray-500">{t('Loading…')}</p>}
        {barren?.length === 0 && <p className="text-sm text-gray-500">{t('None over this period.')}</p>}
        {barren?.length > 0 && (
          <table className="w-full">
            <thead>
              <tr>
                <th className={headClass}>{t('Question')}</th>
                <th className={headClass}>{t('Dimensions')}</th>
                <th className={headClass}>{t('Results')}</th>
                <th className={headClass}>{t('Duration')}</th>
              </tr>
            </thead>
            <tbody>
              {barren.map(row => (
                <tr
                  key={row.id}
                  onClick={() => openDetail(row.id)}
                  className="cursor-pointer border-t border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50"
                >
                  <td className={cellClass}>
                    {row.query}
                    <span className="ml-2 text-xs text-gray-400">{row.username}</span>
                  </td>
                  <td className={cellClass}><code className="text-xs text-gray-500">{(row.dimensions || []).join(', ') || '—'}</code></td>
                  <td className={cellClass}>{row.applicable ? (row.results_count ?? '—') : t('no filter')}</td>
                  <td className={cellClass}>{millis(row.duration_ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel title={t('What is actually used')} hint={t('Which dimensions and facets people reach for, rather than which ones exist.')}>
        {usage === null && <p className="text-sm text-gray-500">{t('Loading…')}</p>}
        {usage && (
          <div className="grid gap-6 sm:grid-cols-2">
            {[['Search dimensions', usage.dimensions, 'searches'], ['AI facets', usage.facets, 'queries']].map(([title, list, unit]) => (
              <div key={title}>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t(title)}</h3>
                {list?.length === 0 && <p className="mt-1 text-sm text-gray-500">{t('None over this period.')}</p>}
                <ul className="mt-1 space-y-0.5">
                  {(list || []).map(row => (
                    <li key={row.dimension || row.field} className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                      <code className="text-xs">{row.dimension || row.field}</code>
                      <span className="tabular-nums text-gray-500">{row[unit]}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Modal open={Boolean(detail)} onClose={() => setDetail(null)} title={t('Search detail')} width="max-w-3xl">
        {detail?.loading && <p className="text-sm text-gray-500">{t('Loading…')}</p>}
        {detail?.failed && <Unavailable>{t('This entry could not be read.')}</Unavailable>}
        {detail?.id && (
          <div className="space-y-4 text-sm">
            <div>
              <p className="text-base text-gray-900 dark:text-white">« {detail.query} »</p>
              <p className="mt-0.5 text-xs text-gray-500">
                {[new Date(detail.created_at).toLocaleString(), detail.username, detail.client].filter(Boolean).join(' · ')}
              </p>
            </div>
            <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                [t('Duration'), millis(detail.duration_ms)],
                [t('Model calls'), detail.model_calls ?? '—'],
                [t('Vocabulary'), detail.vocabulary_cached === null ? '—' : t(detail.vocabulary_cached ? 'cached' : 'loaded')],
                [t('Results'), resultsLabel(detail, detail.events?.find(event => event.type === 'results_failed')?.detail?.code, t)],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="text-xs uppercase text-gray-500">{label}</dt>
                  <dd className="font-semibold text-gray-900 dark:text-white">{value}</dd>
                </div>
              ))}
            </dl>

            {detail.timings && (
              // Le total ne dit pas où part le temps : c'est la ventilation qui distingue un
              // vocabulaire lent d'un modèle lent.
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('Time per stage')}</h3>
                <ul className="mt-1 space-y-0.5">
                  {Object.entries(detail.timings).map(([stage, value]) => (
                    <li key={stage} className="flex justify-between">
                      <code className="text-xs">{stage}</code>
                      <span className="tabular-nums text-gray-500">{millis(value)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {[['Filters', detail.filters], ['Not applied', detail.unresolved_terms],
              ['Clarifications', detail.clarifications], ['Warnings', detail.warnings]]
              .filter(([, value]) => value && Object.keys(value).length > 0)
              .map(([label, value]) => (
                <div key={label}>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t(label)}</h3>
                  <pre className="mt-1 overflow-x-auto rounded bg-gray-50 p-2 text-xs dark:bg-gray-900">
                    {JSON.stringify(value, null, 1)}
                  </pre>
                </div>
              ))}

            {detail.events?.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('What the person did next')}</h3>
                <ul className="mt-1 space-y-0.5">
                  {detail.events.map((event, index) => (
                    <li key={index} className="flex justify-between gap-3">
                      <span className="text-gray-700 dark:text-gray-300">{describeGesture(event, t)}</span>
                      <span className="tabular-nums text-gray-500">
                        +{Math.max(0, Math.round((new Date(event.created_at) - new Date(detail.created_at)) / 1000))} s
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {detail.error && <Unavailable>{detail.error}</Unavailable>}
          </div>
        )}
      </Modal>
    </div>
  );
}
